import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Alert,
} from '@mui/material'
import { useNotify } from '../../components/Notifications'
import * as cobrosApi from '../../api/cobros'

// Arqueo de caja del proceso de cobro abierto.
//
// VALIDADO EN VIVO (2026-06-25, dpyagil/MT): POST /arqueocajaventa con el payload mínimo de abajo
// devuelve 200 y crea el arqueo (id=1 en la prueba), dejando el proceso de cobro ABIERTO
// (solo marca arqueado='S', NO lo cierra).
//
// El cálculo es 100% server-side (ArqueoCajaVentaBusinessImpl.persist2): el backend recorre los
// cobros del proceso + catálogos (ConceptoArqueo, AsignacionMoneda) y completa
// totalConceptos / totalValores / diferencia / detalleArqueos / declaracionEfecs. El front solo
// dispara el arqueo del proceso; NO hay conteo manual de denominaciones en este popup
// (consistente con el comentario del backend: "en el popup emergente no pide descripción").
//
// PENDIENTE DE DATOS (no de payload): en el tenant del MT los catálogos ConceptoArqueo y
// AsignacionMoneda/DenominacionMoneda están vacíos, por eso detalleArqueos/declaracionEfecs
// vuelven []. Al cargarse esos catálogos del tenant, el mismo POST los poblará sin cambios acá.
export default function ArqueoModal({ open, proceso, caja, usrCre, onClose, onDone }) {
  const notify = useNotify()
  const [busy, setBusy] = useState(false)
  const [resultado, setResultado] = useState(null)

  const registrar = async () => {
    if (!proceso?.id) {
      notify.error('No hay un proceso de cobro abierto para arquear.')
      return
    }
    setBusy(true)
    try {
      const hoy = cobrosApi.fechaYMD()
      // Payload @NotNull-completo validado en vivo. idEmpresa/idUnidad/vrTipoArqueo los
      // sobreescribe el backend, pero deben ir presentes para pasar la validación del persist.
      const arq = await cobrosApi.registrarArqueo({
        procesoCobro: { id: proceso.id },
        caja: caja ? { id: caja.id } : null,
        fecha: hoy,
        fecCre: hoy,
        vrTipoArqueo: 'Arqueo definitivo',
        usrCre: usrCre || 'sistema',
        idEmpresa: 1,
        idUnidad: 1,
      })
      setResultado(arq)
      notify.success(`Arqueo registrado (#${arq?.id ?? '—'}).`)
      onDone?.()
    } catch (e) {
      notify.error(e?.response?.data?.[0]?.message || 'No se pudo registrar el arqueo.')
    } finally { setBusy(false) }
  }

  const cerrarModal = () => { setResultado(null); onClose() }

  const fmt = (n) => (n == null ? '—' : new Intl.NumberFormat('es-PY').format(n))

  return (
    <Dialog open={open} onClose={cerrarModal} maxWidth="sm" fullWidth>
      <DialogTitle>Arqueo de caja</DialogTitle>
      <DialogContent dividers>
        {!resultado ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Genera el arqueo del proceso de cobro abierto (#{proceso?.id ?? '—'}). El sistema
              totaliza automáticamente los cobros del proceso y calcula la diferencia. El proceso
              de cobro queda abierto (el arqueo no cierra la caja).
            </Typography>
            {!proceso?.id && (
              <Alert severity="warning">No hay un proceso de cobro abierto para arquear.</Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Alert severity="success">Arqueo #{resultado.id} registrado correctamente.</Alert>
            <Typography variant="body2">Total conceptos (cobrado): <strong>{fmt(resultado.totalConceptos)}</strong></Typography>
            <Typography variant="body2">Total valores (declarado): <strong>{fmt(resultado.totalValores)}</strong></Typography>
            <Typography variant="body2">Diferencia: <strong>{fmt(resultado.diferencia)}</strong></Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={cerrarModal}>{resultado ? 'Cerrar' : 'Cancelar'}</Button>
        {!resultado && (
          <Button variant="contained" color="secondary" onClick={registrar} disabled={busy || !proceso?.id}>
            Registrar arqueo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
